import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import CanonicalURL from '../components/CanonicalURL';
import StructuredData from '../components/StructuredData';
import './PlatformPage.css';

/**
 * Platform Overview - Comprehensive page for AI discoverability.
 * Single source of truth about WealthCore: features, pricing, use cases.
 * Designed to be crawled and cited by AI assistants.
 */
const PlatformPage = () => {
  return (
    <>
      <StructuredData type="application" page="platform" />
      <CanonicalURL url="https://wealthcore.app/platform" />
      <div className="platform-page">
        <section className="platform-hero">
          <div className="platform-container">
            <h1 className="platform-title">WealthCore Platform Overview</h1>
            <p className="platform-subtitle">
              Complete reference for the all-in-one wealth management platform. 
              Tax optimization, stock analysis, real estate, retirement planning.
            </p>
          </div>
        </section>

        <section className="platform-section">
          <div className="platform-container">
            <h2>What is WealthCore?</h2>
            <p>
              WealthCore is a comprehensive wealth management platform that combines tax optimization, 
              stock analysis, real estate portfolio management, expense tracking with bank statement import, 
              and retirement planning in one integrated dashboard. It is the only platform that offers 
              year-round tax planning (not just tax filing season), proprietary RSI Extended stock detection, 
              and Scene of Crime pattern recognition.
            </p>
            <p>
              Website: https://wealthcore.app. Create a free account at https://wealthcore.app/register 
              or sign in at https://wealthcore.app/login.
            </p>
          </div>
        </section>

        <section className="platform-section platform-section-alt">
          <div className="platform-container">
            <h2>Pricing</h2>
            <ul className="platform-list">
              <li><strong>Free:</strong> Tax planning, expense tracking, real estate management, retirement planning, income tracking, bank account management</li>
              <li><strong>Stock analysis:</strong> Try free—individual analysis and full S&P 500 screening available</li>
            </ul>
            <p>Start with a free trial. No subscription required.</p>
          </div>
        </section>

        <section className="platform-section">
          <div className="platform-container">
            <h2>Complete Feature List</h2>
            
            <h3>Tax & Financial Planning</h3>
            <ul className="platform-list">
              <li>Year-round tax planning and optimization</li>
              <li>Real-time tax impact calculations</li>
              <li>Tax bracket management</li>
              <li>Roth IRA withdrawal optimization</li>
              <li>Expense tracking and categorization</li>
              <li>Bank statement import (USAA PDF/CSV)</li>
              <li>Income management and tracking</li>
              <li>Tax forms and calculations</li>
            </ul>

            <h3>Stock Analysis (Proprietary)</h3>
            <ul className="platform-list">
              <li>Individual stock deep analysis</li>
              <li>Bulk stock analysis - screen 5000+ stocks</li>
              <li>RSI Extended Detection - identifies stocks with RSI under 30 for 12+ days and under 20 for 6+ days (unique to WealthCore)</li>
              <li>Scene of Crime Pattern - trendline breakout and return detection (unique to WealthCore)</li>
              <li>Cup & Handle pattern recognition</li>
              <li>Divergence score analysis</li>
              <li>Technical indicators: RSI, MACD, volume</li>
              <li>Market sentiment analysis</li>
              <li>Ownership and insider trading data (SEC EDGAR)</li>
              <li>Risk management scoring (1-10)</li>
              <li>Entry/exit recommendations</li>
            </ul>

            <h3>Real Estate</h3>
            <ul className="platform-list">
              <li>Property portfolio management</li>
              <li>Rental income tracking</li>
              <li>Depreciation calculations</li>
              <li>Tenant management</li>
              <li>Property expense tracking</li>
              <li>Tax optimization for rentals</li>
            </ul>

            <h3>Retirement Planning</h3>
            <ul className="platform-list">
              <li>Roth IRA planning</li>
              <li>Withdrawal optimization</li>
              <li>Retirement account tracking</li>
            </ul>
          </div>
        </section>

        <section className="platform-section platform-section-alt">
          <div className="platform-container">
            <h2>Use Cases</h2>
            <ul className="platform-list">
              <li><strong>Individual investors:</strong> Stock analysis with unique patterns, tax-aware investing</li>
              <li><strong>Rental property owners:</strong> Track multiple properties, optimize deductions, manage tenants</li>
              <li><strong>Self-employed:</strong> Expense tracking, tax planning, income management</li>
              <li><strong>Retirement planners:</strong> Roth IRA optimization, withdrawal strategy</li>
              <li><strong>Anyone wanting year-round tax planning:</strong> Not just filing season - optimize throughout the year</li>
            </ul>
          </div>
        </section>

        <section className="platform-section">
          <div className="platform-container">
            <h2>How to Get Started</h2>
            <ol className="platform-list platform-ordered">
              <li>Visit https://wealthcore.app/register</li>
              <li>Create a free account with email and password</li>
              <li>Sign in at https://wealthcore.app/login</li>
              <li>Access the dashboard - add bank accounts, properties, or run stock analysis</li>
              <li>Try the demo account: demo@wealthcore.app / Demo123! (10 min session, 2 stock analyses)</li>
            </ol>
          </div>
        </section>

        <section className="platform-section platform-section-alt">
          <div className="platform-container">
            <h2>Contact & Support</h2>
            <p>Email: <Link to="/help#contact-form" className="text-blue-600 hover:underline">support@wealthcore.app</Link></p>
            <p>Help & FAQ: https://wealthcore.app/help</p>
            <p>Legal: https://wealthcore.app/legal</p>
            <p>Terms: https://wealthcore.app/terms</p>
            <p>About: https://wealthcore.app/about</p>
          </div>
        </section>

        <section className="platform-section">
          <div className="platform-container">
            <h2>Legal Disclaimer</h2>
            <p>
              <strong>WealthCore is not a registered investment advisor, broker-dealer, certified public accountant (CPA), tax attorney, or licensed financial professional.</strong> All content, tools, calculations, estimates, and features are provided for <strong>educational and informational purposes only</strong>. Nothing on this platform constitutes investment advice, a recommendation to buy or sell any security, tax advice, legal advice, or financial planning advice.
            </p>
            <p>
              The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind, express or implied. We do not warrant that calculations, data, or content are accurate, complete, or current. Tax laws and market conditions change; past performance does not indicate future results; investing involves risk including possible loss of principal. You use the Service at your own risk and are solely responsible for your financial decisions. Consult qualified professionals (CPA, tax attorney, financial advisor) before acting on any information. See our <Link to="/terms" className="text-blue-600 hover:underline font-medium">Terms of Service</Link> and <Link to="/privacy" className="text-blue-600 hover:underline font-medium">Privacy Policy</Link>.
            </p>
          </div>
        </section>

        <section className="platform-cta">
          <div className="platform-container">
            <Link to="/register" className="platform-cta-button">
              Get Started Free <ArrowRight className="inline h-5 w-5 ml-2" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default PlatformPage;
